'use client';

import dynamic from 'next/dynamic';

import { Link } from '@natu/next-link';
import { Skeleton } from '@natu/ui/Skeleton';
import { cn } from '@natu/utils/cn';

import { type JobOfferItem, useJobOfferItems } from './getJobOfferItems';

const Button = dynamic(() => import('@natu/ui/Button').then(mod => mod.Button));

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

type JobOfferCardsProps = {
  className?: string;
  jobOfferItems: string[];
};

export const JobOfferCards = ({ jobOfferItems, className, ...rest }: JobOfferCardsProps) => {
  const { data, isEmpty } = useJobOfferItems({ jobOfferItems });

  if (isEmpty)
    return (
      <p className={cn('text-gray-600', className)}>
        Sorry, there is no job offers in this category
      </p>
    );

  return (
    <ul className={cn('grid grid-cols-1 gap-6 md:grid-cols-2', className)} {...rest}>
      {data.map((item: JobOfferItem) => (
        <li key={item.uuid} className="rounded-2xl border border-gray-200 px-6 pb-8 pt-6">
          {item.tag?.name && (
            <p
              className="mb-1 text-sm font-semibold"
              style={{ color: item.tag.color || '#000000' }}
            >
              {item.tag.name}
            </p>
          )}
          {item.title && <h3 className="mb-4 text-lg font-semibold text-gray-900">{item.title}</h3>}
          {item.description && <p className="mb-3 text-gray-600">{item.description}</p>}
          {item.fullSlug && (
            <Button variant="link" size="link" asChild>
              <Link
                className="text-primary-700 flex items-center gap-x-2 text-[0.813rem] font-semibold uppercase leading-[1.25rem]"
                href={item.fullSlug}
              >
                Check the details
                <DynamicIcon icon="arrowRight" />
              </Link>
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
};

export const JobOfferCardsSkeleton = ({ className }: { className?: string }) => (
  <ul className={cn('grid grid-cols-1 gap-6 md:grid-cols-2', className)}>
    <Skeleton className="h-40 w-full" />
    <Skeleton className="h-40 w-full" />
  </ul>
);
